import { Icon } from '@assembly-js/design-system';
import { useMediaQuery, useTheme } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { z } from 'zod';

import UiClient from 'src/clients/UiClients';
import { PaperClipIcon } from 'src/components/icons';
import { REFERRAL_PAGE_V2, SETTINGS_REFERRALS_PAGE } from 'src/constants';
import {
  API_DOCS_URL,
  GET_STARTED_VIDEO_URL,
  JOIN_DISCOURSE_COMMUNITY_URL,
  PORTAL_UPDATES_URL,
  SUPPORT_EMAIL,
} from 'src/constants/externalUrls';
import { primaryDomainHostname } from 'src/constants/hostnameConsts';
import { FlagsContext, PortalConfigContext, RouteContext } from 'src/context';
import history from 'src/history';
import { useKeyboardShortcuts } from 'src/hooks/useKeyboardShortcuts';
import { usePrefill } from 'src/hooks/usePrefill';
import { FileUploader } from 'src/legacy/components/Files';
import { ModalWrapper } from 'src/legacy/components/Modals';
import { typography13RegularStyle } from 'src/legacy/components/Text';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { BaseTextField } from 'src/legacy/components/TextField';
import { useRouteAccess } from 'src/routes/routeAccess';
import { RootState } from 'src/store';
import { shortcutsSidebarOpen } from 'src/store/shortcuts/slice';
import {
  alertSnackbar,
  toggleHelpCenterModalAction,
  togglePrimarySidebarMobile,
} from 'src/store/ui/actions';
import * as Colors from 'src/theme/colors';

import {
  HelpCenterCommunityIcon,
  HelpCenterHelpDeskIcon,
  HelpCenterReferralsIcon,
  HelpCenterShortcutsIcon,
  HelpCenterSupportIcon,
  HelpCenterUpdatesIcon,
} from './helpCenterIcons';

export interface HelpCenterLineItemProps {
  title: string;
  desc: string;
  linkTo: string;
  internalPath?: string;
  Icon?: () => JSX.Element;
}

const useStyles = makeStyles()((theme) => ({
  root: {
    [theme.breakpoints.down('xs')]: {
      height: '100%',
    },
    [theme.breakpoints.up('sm')]: {
      width: '480px',
    },
    '& .MuiDialogActions-root': {
      '&>div': {
        margin: 0,
        width: '100%',
      },
      '& button': {
        backgroundColor: Colors.BlackHeadings,
        border: `1px solid ${Colors.NonHoverBlackBorder}`,
        color: Colors.white,
        width: '100%',
      },
    },
  },
  lineItem: {
    cursor: 'pointer',
    '& .right-arrow': {
      color: Colors.LightGray,
    },
    '&:hover': {
      '& .right-arrow': {
        color: Colors.BlackHeadings,
      },
    },
  },
  modalContent: {
    '& .MuiInputBase-root .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
  messageBox: {
    '&.MuiOutlinedInput-root.MuiOutlinedInput-multiline': {
      padding: 0,
    },
    '&.MuiOutlinedInput-root.MuiOutlinedInput-multiline textarea': {
      padding: 0,
    },
  },
  messageInput: {
    '&&.MuiOutlinedInput-input': {
      ...typography13RegularStyle,
      padding: 0,
    },
  },
}));

// Schema for help center modal prefilled data
const HelpCenterPrefillSchema = z
  .object({
    body: z.string().optional(), // message content
    // Add any other fields that might be passed to the help center
  })
  .passthrough();
export const HelpCenterModal: React.FC = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { query } = React.useContext(RouteContext);
  const {
    pageAction,
    body: supportMessage,
    title: supprotMessageTitle,
  } = query;
  const { action, prefilledData } = usePrefill(HelpCenterPrefillSchema);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const dispatch = useDispatch();
  const { routeAccess } = useRouteAccess();
  const { EnableNewSidebar, EnableGrowthLoops } =
    React.useContext(FlagsContext);
  // Under growth-loops the program lives in settings, so the Help Center
  // entries point there instead of the retired page.
  const referralsPath = EnableGrowthLoops
    ? SETTINGS_REFERRALS_PAGE.path
    : REFERRAL_PAGE_V2.path;
  const portalConfig = React.useContext(PortalConfigContext);
  const [attachedFileKey, setAttachedFileKey] = React.useState('');
  const [attachedFileFullPath, setAttachedFileFullPath] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [isSendingMessage, setIsSendingMessage] = React.useState(false);
  const [modalInfo, setModalInfo] = React.useState({
    mode: 'HELP',
    title: 'Help',
    description: '',
    action: null,
  });

  const open = useSelector(
    (state: RootState) => state.ui.helpCenterModal.isOpen,
  );
  const isPrimarySidebarOpenOnMobile = useSelector(
    (state: RootState) => state.ui.primarySideBarMobile.isOpen,
  );

  const closeHelpCenterModal = () => {
    dispatch(toggleHelpCenterModalAction(false));
  };

  const switchToContactSupportModal = () => {
    setModalInfo({
      mode: 'CONTACT_SUPPORT',
      title: `${supprotMessageTitle ?? 'Contact support'}`,
      description: `You can also email us at ${SUPPORT_EMAIL}`,
      action: null,
    });
  };
  const handleHelpCenterItemClicked = (
    item: HelpCenterLineItemProps | undefined,
  ) => {
    if (item?.internalPath) {
      history.push(item.internalPath);
      if (isPrimarySidebarOpenOnMobile) {
        dispatch(togglePrimarySidebarMobile({ isOpen: false }));
      }
      closeHelpCenterModal();
      return;
    }
    if (item?.linkTo) {
      window.open(item?.linkTo);
      return;
    }
    if (item?.title === 'Keyboard shortcuts') {
      dispatch(shortcutsSidebarOpen(true));
      // close help center modal when shortcut sidebar is open
      closeHelpCenterModal();
      return;
    }
    switchToContactSupportModal();
  };

  React.useEffect(() => {
    // when page action is contactSupport switch to contact support modal
    const currentAction = action || pageAction;
    const currentMessage = prefilledData?.body || supportMessage;

    if (currentAction === 'contactSupport' && open) {
      setTimeout(() => {
        switchToContactSupportModal();
        // set the message content to the query body param or prefilled data
        if (currentMessage) {
          setMessage(currentMessage);
        }
      }, 100);
    }
  }, [pageAction, action, open, supportMessage, prefilledData]);

  useKeyboardShortcuts([
    {
      id: 'open-help-desk',
      section: 'Help',
      name: 'Open help desk',
      perform: () => {
        handleHelpCenterItemClicked(helpCenterSections.at(0));
      },
    },
    {
      id: 'watch-welcome-video',
      section: 'Help',
      name: 'Explore video tutorials',
      perform: () => window.open(GET_STARTED_VIDEO_URL, '_blank'),
    },
    {
      id: 'contact-support',
      section: 'Help',
      name: 'Contact support',
      perform: () => {
        dispatch(toggleHelpCenterModalAction(true));
        setTimeout(() => switchToContactSupportModal(), 100);
      },
    },
    {
      id: 'join-discourse-community',
      section: 'Miscellaneous',
      name: 'Join our community',
      perform: () => window.open(JOIN_DISCOURSE_COMMUNITY_URL, '_blank'),
    },
    ...(routeAccess.allowReferralPage
      ? [
          {
            id: 'refer-portal',
            section: 'Miscellaneous',
            name: `Refer ${t(($) => $.app_brand_name)}`,
            perform: () => history.push(referralsPath),
          },
        ]
      : []),
    {
      id: 'see-recent-updates',
      section: 'Miscellaneous',
      name: 'See recent updates',
      perform: () => window.open(PORTAL_UPDATES_URL, '_blank'),
    },
    ...(routeAccess.allowSettingsApiPage
      ? [
          {
            id: 'open-api-docs',
            section: 'Miscellaneous',
            name: 'Open API documentation',
            perform: () => window.open(API_DOCS_URL, '_blank'),
          },
        ]
      : []),
    {
      id: 'show-keyboard-shortcuts',
      section: 'Miscellaneous',
      name: 'Show keyboard shortcuts',
      perform: () => dispatch(shortcutsSidebarOpen(true)),
    },
  ]);

  const renderLineItem = (item: HelpCenterLineItemProps, hasBorder = true) => (
    <div
      className={`w-full flex ${classes.lineItem}`}
      onClick={() => handleHelpCenterItemClicked(item)}
      key={item.title}
    >
      <div className="mt-3.5">{item.Icon && <item.Icon />}</div>
      <div
        className="flex w-full items-center justify-between ml-[13px] py-[11px]"
        style={{
          borderBottom: hasBorder
            ? `1px solid ${Colors.DividersAndCardBorders}`
            : 'none',
        }}
      >
        <div>
          <BaseTypography
            fontType="13Medium"
            style={{ color: Colors.BlackHeadings }}
          >
            {item.title}
          </BaseTypography>
          <BaseTypography style={{ color: Colors.GraySmall }}>
            {item.desc}
          </BaseTypography>
        </div>
        <Icon
          icon="ChevronRight"
          className="right-arrow"
          width={9}
          height={9}
        />
      </div>
    </div>
  );

  const messageChangeHandler = (event: any) => {
    const { value } = event.target;
    setMessage(value);
  };

  const renderContactSupportModalContent = ({
    isInputDisabled,
  }: {
    isInputDisabled: boolean;
  }) => (
    <div
      className={`min-h-[194px] flex flex-col justify-between ${classes.modalContent}`}
    >
      <BaseTextField
        InputProps={{
          classes: {
            root: classes.messageBox,
            inputMultiline: classes.messageInput,
          },
        }}
        disabled={isInputDisabled}
        fullWidth
        placeholder="How can we help you?"
        name="message"
        value={message}
        multiline
        rows={7}
        onChange={messageChangeHandler}
        variant="outlined"
        label=""
        autoFocus
      />
    </div>
  );

  React.useEffect(() => {
    setAttachedFileKey('');
    setAttachedFileFullPath('');
    setMessage('');
    setModalInfo({
      mode: 'HELP',
      title: 'Help Center',
      description: '',
      action: null,
    });
  }, [open]);

  const sendEmailHandler = async () => {
    if (!message) {
      dispatch(
        alertSnackbar({
          errorMessage: `Cannot send message without content.`,
        }),
      );
    } else {
      try {
        setIsSendingMessage(true);
        await UiClient.contactSupport(message, attachedFileFullPath);
        dispatch(
          alertSnackbar({
            successMessage: `We'll be in touch with you soon over email.`,
          }),
        );

        closeHelpCenterModal();
      } catch (error) {
        dispatch(
          alertSnackbar({
            errorMessage: 'Sending message failed.',
          }),
        );
      } finally {
        setIsSendingMessage(false);
      }
    }
  };

  const helpCenterSections = [
    {
      Icon: HelpCenterHelpDeskIcon,
      title: t(($) => $.page_help_center.portal_guide.title),
      desc: t(($) => $.page_help_center.portal_guide.description),
      linkTo: `https://www.${primaryDomainHostname.prod}/guide`,
    },
    {
      Icon: HelpCenterCommunityIcon,
      title: t(($) => $.page_help_center.join_discourse_community.title),
      desc: t(($) => $.page_help_center.join_discourse_community.description),
      linkTo: JOIN_DISCOURSE_COMMUNITY_URL,
    },
    {
      Icon: HelpCenterSupportIcon,
      title: t(($) => $.page_help_center.contact_support.title),
      desc: t(($) => $.page_help_center.contact_support.description),
      linkTo: '',
    },
    {
      Icon: HelpCenterUpdatesIcon,
      title: t(($) => $.page_help_center.updates.title),
      desc: t(($) => $.page_help_center.updates.description),
      linkTo: `https://www.${primaryDomainHostname.prod}/updates/`,
    },
    {
      Icon: HelpCenterShortcutsIcon,
      title: t(($) => $.page_help_center.keyboard_shortcuts.title),
      desc: t(($) => $.page_help_center.keyboard_shortcuts.description),
      linkTo: '',
    },
    ...(EnableNewSidebar && routeAccess.allowReferralPage
      ? [
          {
            Icon: HelpCenterReferralsIcon,
            title: t(($) => $.page_help_center.referrals.title),
            desc: t(($) => $.page_help_center.referrals.description),
            linkTo: '',
            internalPath: referralsPath,
          },
        ]
      : []),
  ];

  return open ? (
    <ModalWrapper
      disableSuccessButton={message.length === 0}
      fullScreen={isMobile}
      isLoading={isSendingMessage}
      open={open}
      title={modalInfo.title}
      description={modalInfo.description}
      onClose={closeHelpCenterModal}
      positiveAction={modalInfo.mode !== 'HELP'}
      successButtonLabel="Send message"
      onSuccess={sendEmailHandler}
      width={546}
      footerLeftButtonLabel={
        modalInfo.mode !== 'HELP' && (
          <FileUploader
            fileKey={attachedFileKey}
            onChangeFileKey={(key: string) => setAttachedFileKey(key)}
            prePath={`${portalConfig.portalHeader}/support`}
            label="Attach images, files, or videos"
            startIcon={<PaperClipIcon />}
            useUploadPortalFile
            setS3FilePath={(path: string) => setAttachedFileFullPath(path)}
          />
        )
      }
    >
      {modalInfo.mode === 'HELP' ? (
        <div className="flex flex-col -my-4">
          {helpCenterSections.map((lineItem, index, arr) =>
            renderLineItem(lineItem, arr.length !== index + 1),
          )}
        </div>
      ) : (
        renderContactSupportModalContent({ isInputDisabled: isSendingMessage })
      )}
    </ModalWrapper>
  ) : null;
};
